/**
 * custom-dotcom.js
 * Liquid pull WebGL effect on product card images.
 * No dependencies — pure vanilla WebGL.
 *
 * How it works:
 *  - On mouseenter, a <canvas> is overlaid on the product image.
 *  - The image is loaded as a WebGL texture (Shopify CDN supports CORS).
 *  - A fragment shader displaces UV coordinates toward the cursor using a
 *    Gaussian falloff (strong pull near the cursor, fades outward).
 *  - Spring physics (stiffness + damping) make the pull ease in and
 *    bounce back when the mouse leaves.
 *  - Canvas is removed once the spring fully settles (no idle GPU cost).
 */

(function () {
  'use strict';

  // Touch flag — WebGL hover needs a cursor so it's skipped on touch devices,
  // but the auto holographic shimmer runs everywhere.
  var isTouchDevice = ('ontouchstart' in window);

  // ── Sound effects ─────────────────────────────────────────────────────────────
  var _assetBase = (function () {
    var s = document.querySelector('script[src*="custom-dotcom"]');
    return s ? s.src.replace(/custom-dotcom\.js.*$/, '') : '';
  })();

  var sfxHover = new Audio(_assetBase + 'sfx-hover.wav');
  sfxHover.preload = 'auto';
  sfxHover.volume  = 0.08;

  var sfxClick = new Audio(_assetBase + 'sfx-click.wav');
  sfxClick.preload = 'auto';
  sfxClick.volume  = 0.08;

  var sfxMenuClose = new Audio(_assetBase + 'sfx-menu-close.wav');
  sfxMenuClose.preload = 'auto';
  sfxMenuClose.volume  = 0.08;

  var sfxMenuOpen = new Audio(_assetBase + 'sfx-menu-open.wav');
  sfxMenuOpen.preload = 'auto';
  sfxMenuOpen.volume  = 0.08;

  var sfxInstagram = new Audio(_assetBase + 'sfx-instagram.wav');
  sfxInstagram.preload = 'auto';
  sfxInstagram.volume  = 0.08;

  // Browsers block audio until the first real user gesture (click/keydown).
  // Silently play-then-pause on the first pointerdown so hover SFX are ready.
  // Skip entirely on touch devices — audio effects are desktop-only.
  var audioUnlocked = false;
  function unlockAudio() {
    if (audioUnlocked || isTouchDevice) return;
    audioUnlocked = true;
    [sfxHover, sfxClick, sfxMenuClose, sfxMenuOpen, sfxInstagram].forEach(function (snd) {
      var v = snd.volume;
      snd.volume = 0;
      snd.play().then(function () { snd.pause(); snd.currentTime = 0; snd.volume = v; })
                .catch(function () { snd.volume = v; });
    });
  }
  document.addEventListener('pointerdown', unlockAudio, { once: true });
  // Re-prime after back/forward cache restore
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      audioUnlocked = false;
      document.addEventListener('pointerdown', unlockAudio, { once: true });
    }
  });

  var hoverCooldown = false;

  function playHover() {
    if (isTouchDevice || hoverCooldown) return;
    hoverCooldown = true;
    setTimeout(function () { hoverCooldown = false; }, 300);
    sfxHover.currentTime = 0;
    sfxHover.play().catch(function () {});
  }

  function playClick() {
    if (isTouchDevice) return;
    sfxClick.currentTime = 0;
    sfxClick.play().catch(function () {});
  }

  function playMenuClose() {
    if (isTouchDevice) return;
    sfxMenuClose.currentTime = 0;
    sfxMenuClose.play().catch(function () {});
  }

  function playMenuOpen() {
    if (isTouchDevice) return;
    sfxMenuOpen.currentTime = 0;
    sfxMenuOpen.play().catch(function () {});
  }

  // ── Spring config ────────────────────────────────────────────────────────────
  var STIFFNESS     = 0.07;   // how quickly it chases the target (0..1)
  var DAMPING       = 0.72;   // friction — lower = more bounce (0..1)
  var MAX_STRENGTH  = 0.22;   // max UV displacement (0 = none, 0.2 = very strong)
  var SETTLE_LIMIT  = 0.0004; // stop animating below this threshold
  var BLEED         = 2;      // px canvas bleeds outside parent to hide edge fringe

  // ── GLSL shaders ─────────────────────────────────────────────────────────────
  var VERT = [
    'attribute vec2 aPos;',
    'varying vec2 vUv;',
    'void main() {',
    '  vUv = aPos * 0.5 + 0.5;',
    '  gl_Position = vec4(aPos, 0.0, 1.0);',
    '}'
  ].join('\n');

  // Displacement + holographic foil overlay.
  // The liquid warp runs exactly as before; the foil is a screen-blended
  // rainbow layer whose hue shifts with mouse position and fragment angle,
  // creating a diffraction-grating / holographic sticker look.
  var FRAG = [
    'precision mediump float;',
    'uniform sampler2D uTex;',
    'uniform vec2  uMouse;',  // cursor in UV space (0..1, Y flipped)
    'uniform float uStr;',    // spring strength (0 → MAX_STRENGTH)
    'varying vec2  vUv;',

    // ── Hue → RGB (HSV saturation=1, value=1) ──────────────────────────────
    'vec3 hue2rgb(float h) {',
    '  h = mod(h, 1.0);',
    '  float r = abs(h * 6.0 - 3.0) - 1.0;',
    '  float g = 2.0 - abs(h * 6.0 - 2.0);',
    '  float b = 2.0 - abs(h * 6.0 - 4.0);',
    '  return clamp(vec3(r, g, b), 0.0, 1.0);',
    '}',

    'void main() {',
    // ── Liquid displacement (unchanged) ──────────────────────────────────
    '  vec2  delta   = uMouse - vUv;',
    '  float dist    = length(delta);',
    '  float falloff = exp(-(dist * dist) / 0.18);',   // sigma ≈ 0.32 UV units
    '  vec2  uv      = vUv + delta * falloff * uStr;',
    '  uv = clamp(uv, 0.001, 0.999);',
    '  vec4 texColor = texture2D(uTex, uv);',

    // ── Holographic foil ──────────────────────────────────────────────────
    // Hue = diagonal UV banding (like a diffraction grating) + mouse-driven
    // global shift + per-fragment angle to cursor for radial rainbow spread.
    '  float angle    = atan(delta.y, delta.x);',
    '  float holoPhase = (vUv.x - vUv.y) * 2.0',       // diagonal banding
    '                  + uMouse.x * 3.0 - uMouse.y * 1.5', // mouse shifts hue
    '                  + angle * 0.35;',                // radial spread
    '  vec3 rainbow   = hue2rgb(mod(holoPhase, 1.0));',

    // Foil fades off faster than the displacement (wider sigma = 0.6)
    // and is gated by the spring so it builds/fades with the liquid effect.
    // Luminance-scaling keeps the effect subtle on dark/black shirts while
    // preserving the full shimmer on bright/white ones.
    '  float holoFalloff = exp(-(dist * dist) / 0.5);',
    '  float luma        = dot(texColor.rgb, vec3(0.299, 0.587, 0.114));',
    '  float foilAlpha   = holoFalloff * clamp(uStr / 0.22, 0.0, 1.0) * 0.55 * luma;',

    // Screen blend: light adds to the image without blowing out dark pixels
    '  vec3 result = 1.0 - (1.0 - texColor.rgb) * (1.0 - rainbow * foilAlpha);',
    '  gl_FragColor = vec4(result, texColor.a);',
    '}'
  ].join('\n');

  // ── Helpers ──────────────────────────────────────────────────────────────────
  function compileShader(gl, type, src) {
    var sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    return sh;
  }

  // ── LiquidEffect class ───────────────────────────────────────────────────────
  var activeEffect = null; // only one WebGL context alive at a time

  function LiquidEffect(wrapperEl, mediaEl) {
    this.wrapper = wrapperEl; // .card-wrapper — receives mouse events
    this.el      = mediaEl;   // .card__media  — where the canvas is drawn
    this.canvas  = null;
    this.gl      = null;
    this.uMouse  = null;
    this.uStr    = null;
    this.tex     = null;
    this.raf     = null;

    // Spring state
    this.str     = 0;
    this.vel     = 0;
    this.target  = 0;
    this.alive   = false;

    // Mouse UV (Y flipped for WebGL convention)
    this.mx = 0.5;
    this.my = 0.5;

    // Events on .card-wrapper — it receives events that the ::after overlay swallows
    var self = this;
    wrapperEl.addEventListener('mouseenter', function (e) { self._enter(e); });
    wrapperEl.addEventListener('mousemove',  function (e) { self._move(e);  });
    wrapperEl.addEventListener('mouseleave', function ()  { self._leave();  });
    wrapperEl.addEventListener('click', function (e) {
      // Find the link being clicked (image overlay or text link)
      var link = e.target.closest ? e.target.closest('a[href]') : null;
      var href = link ? link.href : null;
      if (!href) {
        var fallback = wrapperEl.querySelector('a.full-unstyled-link');
        href = fallback ? fallback.href : null;
      }
      if (!href) return;
      e.preventDefault();
      playClick();
      setTimeout(function () { window.location.href = href; }, 500);
    });

    // Clicks on the image pass through the canvas (pointer-events:none) and land
    // on .card__media which has no link. Inject a real <a> overlay so the browser
    // shows the URL on hover and navigates on click.
    var cardLink = wrapperEl.querySelector('a.full-unstyled-link');
    if (cardLink) {
      if (getComputedStyle(mediaEl).position === 'static') mediaEl.style.position = 'relative';
      var linkOverlay = document.createElement('a');
      linkOverlay.href = cardLink.href;
      linkOverlay.setAttribute('aria-hidden', 'true');
      linkOverlay.setAttribute('tabindex', '-1');
      linkOverlay.style.cssText = 'position:absolute;inset:0;z-index:2;display:block;';
      mediaEl.appendChild(linkOverlay);
    }

    // Cursor hand — JS-positioned so it always sits at the vertical centre of
    // .card__information regardless of card height or viewport size.
    if (getComputedStyle(wrapperEl).position === 'static') {
      wrapperEl.style.position = 'relative';
    }
    var handEl = document.createElement('span');
    handEl.className = 'card-hand';
    wrapperEl.appendChild(handEl);
    this._handEl = handEl;
    this._positionHand();
  }

  // Place the hand element at the vertical centre of .card__information,
  // horizontally centred on the card. Called on every mouseenter so it
  // re-measures after any layout change (resize, lazy-load, etc.).
  LiquidEffect.prototype._positionHand = function () {
    var hand = this._handEl;
    if (!hand) return;
    // There are two .card__information elements: one inside .card__inner
    // (image overlay, hidden/zero-height in standard style) and one below
    // the image (the visible one). Pick the first with actual height.
    var infoEls = this.wrapper.querySelectorAll('.card__information');
    var info = null;
    for (var i = 0; i < infoEls.length; i++) {
      if (infoEls[i].offsetHeight > 0) { info = infoEls[i]; break; }
    }
    if (!info) return;
    var wRect = this.wrapper.getBoundingClientRect();
    var iRect = info.getBoundingClientRect();
    // top offset of the info block's centre relative to .card-wrapper
    var midY = (iRect.top - wRect.top) + iRect.height / 2;
    hand.style.top = (midY - 52) + 'px'; // 52 = half of 105px hand height
  };

  LiquidEffect.prototype._enter = function (e) {
    // Tear down whichever card was previously active
    if (activeEffect && activeEffect !== this) activeEffect._destroy();
    activeEffect = this;

    playHover();
    this._positionHand();
    if (this._handEl) this._handEl.style.opacity = '1';

    this.alive  = true;
    this.target = MAX_STRENGTH;

    if (!this.canvas) this._init();
    this._move(e);
    if (!this.raf) this._tick();
  };

  LiquidEffect.prototype._move = function (e) {
    var r = this.el.getBoundingClientRect();
    this.mx = (e.clientX - r.left) / r.width;
    this.my = 1.0 - (e.clientY - r.top) / r.height;
  };

  LiquidEffect.prototype._leave = function () {
    this.alive  = false;
    this.target = 0; // spring back to resting
    if (this._handEl) this._handEl.style.opacity = '0';
  };

  LiquidEffect.prototype._init = function () {
    var img = this.el.querySelector('img');
    if (!img) return;
    this.img = img;

    var dpr = window.devicePixelRatio || 1;
    var r = this.el.getBoundingClientRect();
    var c = document.createElement('canvas');
    c.width  = Math.round(r.width  * dpr);
    c.height = Math.round(r.height * dpr);
    c.style.position       = 'absolute';
    c.style.top            = '0';
    c.style.left           = '0';
    c.style.width          = '100%';
    c.style.height         = '100%';
    c.style.pointerEvents  = 'none';
    c.style.zIndex         = '1';
    c.style.borderRadius   = 'inherit';
    c.style.outline        = 'none';
    c.style.border         = 'none';
    c.style.boxShadow      = 'none';
    c.style.display        = 'block';
    c.style.visibility     = 'hidden'; // show only once texture is loaded

    if (getComputedStyle(this.el).position === 'static') {
      this.el.style.position = 'relative';
    }
    this.el.appendChild(c);
    this.canvas = c;

    var gl = c.getContext('webgl', { antialias: false, alpha: true, premultipliedAlpha: false }) ||
             c.getContext('experimental-webgl', { antialias: false, alpha: true, premultipliedAlpha: false });
    if (!gl) return;
    this.gl = gl;

    // Build shader program
    var prog = gl.createProgram();
    gl.attachShader(prog, compileShader(gl, gl.VERTEX_SHADER,   VERT));
    gl.attachShader(prog, compileShader(gl, gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    gl.useProgram(prog);

    // Full-screen quad (two triangles)
    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
      -1, -1,   1, -1,   -1,  1,
       1, -1,   1,  1,   -1,  1
    ]), gl.STATIC_DRAW);
    var aPos = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    // Cache uniform locations
    this.uMouse = gl.getUniformLocation(prog, 'uMouse');
    this.uStr   = gl.getUniformLocation(prog, 'uStr');

    // Load image as WebGL texture
    // currentSrc gives the responsive-chosen URL from srcset
    var src  = img.currentSrc || img.src;
    var self = this;
    var ti   = new Image();
    ti.crossOrigin = 'anonymous';
    ti.onload = function () {
      console.log('[dotcom] texture loaded OK — WebGL effect active');
      var t = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, t);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true); // match canvas Y direction
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, ti);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      self.tex = t;
      // Hide the original <img> so it doesn't show through, then reveal canvas
      if (self.img) self.img.style.opacity = '0';
      if (self.canvas) self.canvas.style.visibility = 'visible';
    };
    ti.onerror = function () {
      console.error('[dotcom] texture load FAILED (likely CORS) for:', ti.src);
      self._destroy();
    };
    ti.src = src;
  };

  LiquidEffect.prototype._tick = function () {
    var self = this;

    // Spring step
    this.vel += (this.target - this.str) * STIFFNESS;
    this.vel *= DAMPING;
    this.str += this.vel;

    // Render frame
    if (this.gl && this.tex) {
      var gl = this.gl;
      gl.viewport(0, 0, this.canvas.width, this.canvas.height);
      gl.uniform2f(this.uMouse, this.mx, this.my);
      gl.uniform1f(this.uStr,   this.str);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    }

    // Check if fully settled after mouse left — fade out gracefully
    if (!this.alive &&
        Math.abs(this.str) < SETTLE_LIMIT &&
        Math.abs(this.vel) < SETTLE_LIMIT) {
      this._fadeOut();
      return;
    }

    this.raf = requestAnimationFrame(function () { self._tick(); });
  };

  // Graceful exit: img fades back in hidden behind the canvas,
  // then canvas is removed instantly — background never shows through.
  LiquidEffect.prototype._fadeOut = function () {
    if (this.raf) { cancelAnimationFrame(this.raf); this.raf = null; }
    if (activeEffect === this) activeEffect = null;

    var canvas = this.canvas;
    var img    = this.img;

    this.canvas = null;
    this.gl     = null;
    this.tex    = null;
    this.str    = 0;
    this.vel    = 0;
    this.alive  = false;

    if (!canvas) return;

    // Fade img back in underneath the opaque canvas — user sees nothing change
    if (img) {
      img.style.transition = 'opacity 0.15s ease';
      img.style.opacity    = '1';
    }

    // Once img is fully restored, remove canvas in one frame — no gap, no flash
    setTimeout(function () {
      if (canvas && canvas.parentNode) canvas.parentNode.removeChild(canvas);
      if (img) { img.style.transition = ''; }
    }, 160);
  };

  LiquidEffect.prototype._destroy = function () {
    if (this.raf)    { cancelAnimationFrame(this.raf); this.raf = null; }
    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
    // Restore the original image
    if (this.img) this.img.style.opacity = '';
    this.canvas = null;
    this.gl     = null;
    this.tex    = null;
    this.str    = 0;
    this.vel    = 0;
    this.alive  = false;
    if (activeEffect === this) activeEffect = null;
  };

  // ── Initialise on all product card wrappers ──────────────────────────────────
  function init() {
    var wrappers = document.querySelectorAll('.card-wrapper');
    console.log('[dotcom] liquid effect ready — found', wrappers.length, 'card-wrappers');
    wrappers.forEach(function (wrapper) {
      var media = wrapper.querySelector('.card__media');
      if (!media) return; // skip text-only cards
      new LiquidEffect(wrapper, media);
    });
  }

  // ── Auto holographic shimmer (mobile-friendly idle effect) ───────────────────
  // Every 10 s a random card gets a ~1 s diagonal rainbow sweep drawn on a
  // Canvas 2D overlay — same technique as the WebGL hover effect so it is
  // guaranteed visible regardless of theme stacking-context quirks.
  function initAutoHolo() {
    // Window-scoped flag so Shopify section re-renders (which re-execute this
    // IIFE) don't spin up a second independent shimmer loop.
    if (window._dotcomHoloInitialized) return;
    window._dotcomHoloInitialized = true;
    var medias = Array.prototype.slice.call(
      document.querySelectorAll('.card-wrapper .card__media')
    );
    if (medias.length === 0) return;

    // Ensure positioning so the canvas anchors correctly
    medias.forEach(function (media) {
      if (getComputedStyle(media).position === 'static') {
        media.style.position = 'relative';
      }
    });

    // Shuffled queue — visit every card once before reshuffling
    var queue   = [];
    var lastIdx = -1;

    function refillQueue() {
      queue = medias.map(function (_, i) { return i; });
      for (var i = queue.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var tmp = queue[i]; queue[i] = queue[j]; queue[j] = tmp;
      }
      // Avoid back-to-back repeat across reshuffles
      if (queue.length > 1 && queue[queue.length - 1] === lastIdx) {
        var swap = Math.floor(Math.random() * (queue.length - 1));
        var t2 = queue[queue.length - 1];
        queue[queue.length - 1] = queue[swap];
        queue[swap] = t2;
      }
    }

    function nextIdx() {
      if (queue.length === 0) refillQueue();
      return queue.pop();
    }

    // Metallic holographic sweep — trading-card foil style.
    // The gradient is clipped to the shirt's own pixels using destination-in
    // compositing with the product image (which is a transparent PNG), so the
    // effect never bleeds onto the background.
    function playHoloSweep(media) {
      if (media._holoActive) return; // animation already running on this card
      var img = media.querySelector('img');
      if (!img || !img.complete || !img.naturalWidth) return;
      media._holoActive = true;

      var dpr  = window.devicePixelRatio || 1;
      var rect = media.getBoundingClientRect();
      var W    = rect.width;
      var H    = rect.height;
      if (W === 0 || H === 0) return;

      var canvas = document.createElement('canvas');
      canvas.width  = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
      canvas.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;' +
                             'pointer-events:none;z-index:4;border-radius:inherit;';
      media.appendChild(canvas);

      var ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);

      // Replicate CSS object-fit:cover; object-position:center so the mask
      // aligns exactly with how the browser renders the <img>.
      var imgAspect = img.naturalWidth / img.naturalHeight;
      var canvasAspect = W / H;
      var drawW, drawH;
      if (imgAspect > canvasAspect) { drawH = H; drawW = H * imgAspect; }
      else                          { drawW = W; drawH = W / imgAspect; }
      var drawX = (W - drawW) / 2;
      var drawY = (H - drawH) / 2;

      var DURATION = 950;
      var BAND     = W * 0.36;
      var start    = null;

      function frame(ts) {
        if (!start) start = ts;
        var t = Math.min((ts - start) / DURATION, 1.0);

        // Fade envelope — soft in/out
        var env = t < 0.15 ? t / 0.15 : (t > 0.78 ? (1.0 - t) / 0.22 : 1.0);

        // Linear sweep, no position easing — even speed looks more like light
        var cx = -BAND * 1.8 + t * (W + BAND * 3.6);

        ctx.clearRect(0, 0, W, H);
        ctx.save();
        ctx.translate(W / 2, H / 2);
        ctx.rotate(-0.20); // ~11.5° diagonal tilt
        ctx.translate(-W / 2, -H / 2);

        var d = W + H;

        // Layer 1 — broad silver/gold metallic base (the "foil" sheen)
        var g1 = ctx.createLinearGradient(cx - BAND * 2, 0, cx + BAND * 2, 0);
        g1.addColorStop(0.00, 'rgba(0,0,0,0)');
        g1.addColorStop(0.25, 'rgba(180,160,255,' + (env * 0.06).toFixed(3) + ')');
        g1.addColorStop(0.50, 'rgba(255,255,255,' + (env * 0.14).toFixed(3) + ')'); // silver
        g1.addColorStop(0.75, 'rgba(255,210,120,' + (env * 0.06).toFixed(3) + ')');
        g1.addColorStop(1.00, 'rgba(0,0,0,0)');
        ctx.fillStyle = g1;
        ctx.fillRect(-d, -d, W + d * 2, H + d * 2);

        // Layer 2 — narrow prismatic spectral band (the iridescent detail)
        var g2 = ctx.createLinearGradient(cx - BAND, 0, cx + BAND, 0);
        g2.addColorStop(0.00, 'rgba(0,0,0,0)');
        g2.addColorStop(0.07, 'rgba(160,0,255,'  + (env * 0.11).toFixed(3) + ')');
        g2.addColorStop(0.18, 'rgba(0,80,255,'   + (env * 0.11).toFixed(3) + ')');
        g2.addColorStop(0.28, 'rgba(0,210,220,'  + (env * 0.11).toFixed(3) + ')');
        g2.addColorStop(0.38, 'rgba(50,255,100,' + (env * 0.09).toFixed(3) + ')');
        g2.addColorStop(0.50, 'rgba(255,255,255,' + (env * 0.20).toFixed(3) + ')'); // specular
        g2.addColorStop(0.62, 'rgba(255,230,0,'  + (env * 0.09).toFixed(3) + ')');
        g2.addColorStop(0.72, 'rgba(255,100,0,'  + (env * 0.11).toFixed(3) + ')');
        g2.addColorStop(0.82, 'rgba(255,20,100,' + (env * 0.11).toFixed(3) + ')');
        g2.addColorStop(0.92, 'rgba(160,0,255,'  + (env * 0.07).toFixed(3) + ')');
        g2.addColorStop(1.00, 'rgba(0,0,0,0)');
        ctx.fillStyle = g2;
        ctx.fillRect(-d, -d, W + d * 2, H + d * 2);

        ctx.restore();

        // Clip gradient to shirt pixels only — destination-in keeps the gradient
        // only where the PNG image is opaque, erasing the transparent background.
        try {
          ctx.globalCompositeOperation = 'destination-in';
          ctx.drawImage(img, drawX, drawY, drawW, drawH);
          ctx.globalCompositeOperation = 'source-over';
        } catch (e) {
          // CORS failure — silently clean up
          if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
          media._holoActive = false;
          return;
        }

        if (t < 1.0) {
          requestAnimationFrame(frame);
        } else {
          if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
          media._holoActive = false;
        }
      }

      requestAnimationFrame(frame);
    }

    // Use a single tracked timer so we can cancel it when the tab is hidden,
    // preventing a backlog of animations firing all at once on return.
    var holoTimer = null;

    function scheduleNext(delay) {
      if (holoTimer) clearTimeout(holoTimer);
      holoTimer = setTimeout(triggerNext, delay || 10000);
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        // Tab hidden — cancel any pending trigger so time doesn't accumulate
        if (holoTimer) { clearTimeout(holoTimer); holoTimer = null; }
      } else {
        // Tab visible again — restart the countdown fresh
        scheduleNext(10000);
      }
    });

    // Back/forward cache restore — visibilitychange may not fire on all browsers,
    // so explicitly restart the timer here too. scheduleNext clears any duplicate.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) scheduleNext(10000);
    });

    function triggerNext() {
      var idx     = nextIdx();
      lastIdx     = idx;
      var media   = medias[idx];
      var wrapper = media.closest('.card-wrapper');

      // Skip if being hovered on desktop (WebGL effect already running)
      if (wrapper && wrapper.matches(':hover')) {
        scheduleNext();
        return;
      }

      playHoloSweep(media);
      scheduleNext();
    }

    // First shimmer after 2 s so the page has settled
    scheduleNext(2000);
  }

  // ── Menu-close sound hooks ───────────────────────────────────────────────────
  function initMenuCloseSfx() {
    // Logo / home link — preventDefault so the sound has time to play before
    // the page unloads; navigate after the same 500 ms used on card clicks.
    document.querySelectorAll('a.header__heading-link').forEach(function (link) {
      link.addEventListener('click', function (e) {
        e.preventDefault();
        var href = link.href;
        playMenuClose();
        setTimeout(function () { window.location.href = href; }, 500);
      });
    });

    // Cart drawer — X button, empty-cart X button, and overlay (click outside)
    // don't navigate, so they just play the sound immediately.
    document.addEventListener('click', function (e) {
      if (e.target.closest('.drawer__close') || e.target.closest('#CartDrawer-Overlay')) {
        playMenuClose();
      }
    });

    // "Back to t-shirts" navigates to the homepage — same 500 ms delay so the
    // sound isn't cut off by the page unload.
    document.addEventListener('click', function (e) {
      var backLink = e.target.closest('[data-dotcom-back]');
      if (!backLink) return;
      e.preventDefault();
      playMenuClose();
      setTimeout(function () { window.location.href = backLink.href; }, 500);
    });

    // Escape key closes the cart drawer too
    document.addEventListener('keyup', function (e) {
      if (e.code === 'Escape' && document.querySelector('cart-drawer.active')) {
        playMenuClose();
      }
    });

    // Cart open — watch for the 'active' class being added to <cart-drawer>.
    // MutationObserver catches every trigger: icon click, add-to-cart, etc.
    var cartDrawerEl = document.querySelector('cart-drawer');
    if (cartDrawerEl) {
      new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          if (m.attributeName === 'class') {
            var wasActive = m.oldValue && m.oldValue.indexOf('active') !== -1;
            var isActive  = cartDrawerEl.classList.contains('active');
            if (!wasActive && isActive) playMenuOpen();
          }
        });
      }).observe(cartDrawerEl, { attributes: true, attributeOldValue: true });
    }
  }

  // ── Mute toggle button ────────────────────────────────────────────────────────
  // Injected via JS to avoid Shopify's section-group template cache.
  // Desktop only — no sounds play on touch devices so we skip entirely.
  // pixel art like the basket (snippets/dotcom-icon-basket.liquid): a speaker
  // with its sound waves, and with a cross
  var SVG_SOUND_ON =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 12" width="30" height="24" shape-rendering="crispEdges" aria-hidden="true" focusable="false" class="icon icon-dotcom-sound-on"><path fill="currentColor" d="M6 0h1v1h-1zM5 1h2v1h-2zM11 1h1v1h-1zM4 2h3v1h-3zM12 2h1v1h-1zM3 3h4v1h-4zM8 3h1v1h-1zM13 3h1v1h-1zM0 4h7v1h-7zM9 4h1v1h-1zM13 4h1v1h-1zM0 5h7v1h-7zM9 5h1v1h-1zM13 5h1v1h-1zM0 6h7v1h-7zM9 6h1v1h-1zM13 6h1v1h-1zM0 7h7v1h-7zM9 7h1v1h-1zM13 7h1v1h-1zM3 8h4v1h-4zM8 8h1v1h-1zM13 8h1v1h-1zM4 9h3v1h-3zM12 9h1v1h-1zM5 10h2v1h-2zM11 10h1v1h-1zM6 11h1v1h-1z"/></svg>';

  var SVG_SOUND_OFF =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 12" width="30" height="24" shape-rendering="crispEdges" aria-hidden="true" focusable="false" class="icon icon-dotcom-sound-off"><path fill="currentColor" d="M6 0h1v1h-1zM5 1h2v1h-2zM4 2h3v1h-3zM3 3h4v1h-4zM8 3h1v1h-1zM13 3h1v1h-1zM0 4h7v1h-7zM9 4h1v1h-1zM12 4h1v1h-1zM0 5h7v1h-7zM10 5h2v1h-2zM0 6h7v1h-7zM10 6h2v1h-2zM0 7h7v1h-7zM9 7h1v1h-1zM12 7h1v1h-1zM3 8h4v1h-4zM8 8h1v1h-1zM13 8h1v1h-1zM4 9h3v1h-3zM5 10h2v1h-2zM6 11h1v1h-1z"/></svg>';

  function initMuteButton() {
    var cart = document.getElementById('cart-icon-bubble');
    if (!cart) return;

    var btn = document.createElement('button');
    btn.id        = 'dotcom-mute-btn';
    btn.type      = 'button';
    btn.className = 'header__icon header__icon--sound link focus-inset';
    btn.setAttribute('aria-label', 'Mute sound');
    btn.innerHTML =
      '<span class="svg-wrapper svg-sound-on">'  + SVG_SOUND_ON  + '</span>' +
      '<span class="svg-wrapper svg-sound-off">' + SVG_SOUND_OFF + '</span>' +
      '<span class="visually-hidden">Toggle sound</span>';

    cart.parentNode.insertBefore(btn, cart);
    btn.style.display = 'flex';

    var allSounds = [sfxHover, sfxClick, sfxMenuClose, sfxMenuOpen, sfxInstagram];
    var muted = localStorage.getItem('dotcom-muted') === '1';

    function applyMute(state) {
      muted = state;
      allSounds.forEach(function (snd) { snd.muted = muted; });
      if (muted) {
        btn.setAttribute('data-muted', '');
        btn.setAttribute('aria-label', 'Unmute sound');
      } else {
        btn.removeAttribute('data-muted');
        btn.setAttribute('aria-label', 'Mute sound');
      }
      localStorage.setItem('dotcom-muted', muted ? '1' : '0');
    }

    applyMute(muted);

    btn.addEventListener('click', function () {
      applyMute(!muted);
    });
  }

  // ── Open all Instagram links in a new tab + play sound ───────────────────────
  function instagramNewTab() {
    document.querySelectorAll('a[href*="instagram.com"]').forEach(function (link) {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
      link.addEventListener('click', function () {
        if (isTouchDevice) return;
        sfxInstagram.currentTime = 0;
        sfxInstagram.play().catch(function () {});
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      if (!isTouchDevice) { init(); initMuteButton(); } // desktop only
      initAutoHolo();             // auto shimmer — all devices
      initMenuCloseSfx();         // menu-close sound — desktop only (guarded inside)
      instagramNewTab();
    });
  } else {
    if (!isTouchDevice) { init(); initMuteButton(); }
    initAutoHolo();
    initMenuCloseSfx();
    instagramNewTab();
  }
})();
